import React from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Trash2, Plus } from "lucide-react";
import { useAuth, hasPerm } from "@/context/AuthContext";
import { useResourceList } from "@/hooks/useResource";
import { PageHeader } from "@/components/kit/PageHeader";
import { DataTablePro } from "@/components/kit/DataTablePro";
import { SafeDeleteDialog } from "@/components/kit/SafeDeleteDialog";
import { useBulkActions, BulkDeleteOffersDialog } from "@/components/kit/bulk";
import { Button } from "@/components/ui/button";
import { fmtMoney, fmtDate, pubId } from "@/lib/format";
import { useTenantColumn } from "@/hooks/useTenantColumn";
import { useTenants } from "@/hooks/useTenants";
import { AdvertiserDrawer } from "@/drawers/AdvertiserDrawer";

export default function Advertisers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [drawer, setDrawer] = React.useState({ open: false, item: null });
  const [del, setDel] = React.useState({ open: false, item: null });
  const { data, isLoading } = useResourceList("advertisers", {});
  const tcol = useTenantColumn("advertisers");
  const { data: tenantsData } = useTenants();
  const bulk = useBulkActions("advertisers", { noun: "advertiser" });

  const items = data?.items || [];
  const transferOptions = items.map((a) => ({ value: a.id, label: a.name }));

  const openOffers = (e, row) => {
    e.stopPropagation();
    navigate(`/offers?advertiser_id=${row.id}`);
  };

  const columns = [
    { key: "id", label: "ID", mono: true, render: (r) => <span className="text-muted-foreground">{pubId(r)}</span> },
    { key: "name", label: "Name", sortable: true, render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
    { key: "contact", label: "Contact", render: (r) => <span className="text-muted-foreground">{r.contact}</span> },
    { key: "telegram", label: "Telegram", exportValue: (r) => r.telegram || "", render: (r) => {
      const h = String(r.telegram || "").trim().replace(/^@/, "").replace(/^(https?:\/\/)?(t\.me|telegram\.me)\//i, "");
      return h ? (
        <a href={`https://t.me/${h}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
          data-testid={`advertiser-tg-${r.id}`} className="text-[#60A5FA] underline-offset-2 hover:underline">@{h}</a>
      ) : <span className="text-muted-foreground/40">—</span>;
    } },
    { key: "email", label: "Email", render: (r) => <span className="text-muted-foreground">{r.email}</span> },
    { key: "active_offers", label: "Active offers", align: "right", sortable: true, sortValue: (r) => Number(r.active_offers || 0), exportValue: (r) => r.active_offers || 0,
      render: (r) => (r.active_offers ? (
        <button data-testid={`advertiser-offers-${r.id}`} onClick={(e) => openOffers(e, r)}
          className="font-mono text-sm text-[#60A5FA] underline-offset-2 hover:underline">{r.active_offers}</button>
      ) : <span className="font-mono text-muted-foreground">0</span>) },
    { key: "revenue", label: "Revenue", align: "right", mono: true, sortable: true, sortValue: (r) => Number(r.revenue || 0), exportValue: (r) => r.revenue, render: (r) => fmtMoney(r.revenue) },
    { key: "created_at", label: "Created", sortable: true, render: (r) => fmtDate(r.created_at) },
  ];
  const filters = [
    ...(tcol.show ? [{ key: "tenant_id", label: "Tenant", getValue: (r) => r.tenant_id, options: (tenantsData?.items || []).map((t) => ({ value: t.internalId, label: t.name })) }] : []),
  ];
  const rowActions = (row) => [
    { label: "Edit", icon: Pencil, onClick: () => setDrawer({ open: true, item: row }), testId: `edit-advertiser-${row.id}` },
    ...(hasPerm(user, "advertisers.delete") ? [{ label: "Delete", icon: Trash2, danger: true, onClick: () => setDel({ open: true, item: row }), testId: `delete-advertiser-${row.id}` }] : []),
  ];

  return (
    <div className="space-y-5">
      <PageHeader title="Advertisers" subtitle="Direct advertisers and networks." />
      <DataTablePro testId="advertisers-table" columns={tcol.inject(columns)} rows={items} loading={isLoading}
        filters={filters} searchKeys={["name", "contact", "email", "tenant_seq"]} searchPlaceholder="Search by ID, name, contact or email..." exportName="advertisers"
        onRowClick={(r) => setDrawer({ open: true, item: r })}
        rowActions={hasPerm(user, "advertisers.edit") ? rowActions : null} emptyText="No advertisers found."
        selectable={hasPerm(user, "advertisers.delete")} onBulkAction={bulk.handleBulk} bulkActions={["delete"]}
        toolbarRight={hasPerm(user, "advertisers.create") && (
          <Button onClick={() => setDrawer({ open: true, item: null })} data-testid="add-advertiser-btn" className="h-9 gap-1.5 bg-white text-black hover:bg-white/90"><Plus className="h-4 w-4" /> Add Advertiser</Button>
        )} />
      <AdvertiserDrawer open={drawer.open} item={drawer.item} onOpenChange={(o) => setDrawer((d) => ({ ...d, open: o }))} />
      <SafeDeleteDialog open={del.open} item={del.item} resource="advertisers" transferOptions={transferOptions}
        onOpenChange={(o) => setDel((d) => ({ ...d, open: o }))} />
      <BulkDeleteOffersDialog pending={bulk.pending} busy={bulk.busy} noun="advertiser"
        onCancel={() => bulk.setPending(null)} onConfirm={bulk.confirmDisableDelete} />
    </div>
  );
}
